import React, { useState, useEffect, useCallback } from 'react';
import { fetchSpecialTimes, createSpecialTime, updateSpecialTime, deleteSpecialTime } from './api';
import { useLoginContext } from './LoginContext';

const SpecialTimesModal = ({ isOpen, onClose }) => {
  const { user } = useLoginContext();
  const [specialTimes, setSpecialTimes] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    color: '#FFA07A',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Vordefinierte Farben für Special Times
  const predefinedColors = [
    { color: '#FFA07A', name: 'Lachs' },
    { color: '#f7e5e7ff', name: 'Rosa' },
    { color: '#cefdceff', name: 'Hellgrün' },
    { color: '#bbecffff', name: 'Himmelblau' },
    { color: '#fdd7fdff', name: 'Pflaume' },
    { color: '#faf6ceff', name: 'Khaki' },
    { color: '#fadba1ff', name: 'Orange' },
    { color: '#b9fdf6ff', name: 'Türkis' }
  ];

  const loadSpecialTimes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await fetchSpecialTimes(user?.id);
      if (result.error) {
        setError(result.error);
      } else {
        setSpecialTimes(result || []);
      }
    } catch (err) {
      setError('Fehler beim Laden der Special Times');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Lade Special Times beim Öffnen des Modals
  useEffect(() => {
    if (isOpen) {
      loadSpecialTimes();
    }
  }, [isOpen, loadSpecialTimes]);

  const resetForm = () => {
    setFormData({
      name: '',
      start_date: '',
      end_date: '',
      color: '#FFA07A',
      description: ''
    });
    setEditingItem(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      start_date: item.start_date,
      end_date: item.end_date,
      color: item.color,
      description: item.description || ''
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.start_date || !formData.end_date) {
      setError('Bitte alle Felder ausfüllen');
      return;
    }

    if (formData.start_date > formData.end_date) {
      setError('Startdatum muss vor Enddatum liegen');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let result;
      if (editingItem) {
        result = await updateSpecialTime(editingItem.id, formData, user?.id);
      } else {
        result = await createSpecialTime(formData, user?.id);
      }

      if (result.error) {
        setError(result.error);
      } else {
        await loadSpecialTimes();
        resetForm();
      }
    } catch (err) {
      setError('Netzwerkfehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diese Special Time löschen möchten?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await deleteSpecialTime(id, user?.id);
      
      if (result.error) {
        setError(result.error);
      } else {
        await loadSpecialTimes();
        if (editingItem && editingItem.id === id) {
          resetForm();
        }
      }
    } catch (err) {
      setError('Netzwerkfehler beim Löschen');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  if (!isOpen) return null;

  // Nur für angemeldete Benutzer verfügbar
  if (!user) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '400px',
          textAlign: 'center',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }}>
          <h3 style={{ marginTop: 0, color: '#333' }}>Anmeldung erforderlich</h3>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Für die Verwaltung von Special Times müssen Sie angemeldet sein.
          </p>
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 20px',
              cursor: 'pointer'
            }}
          >
            OK
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        minWidth: '800px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '2px solid #eee',
          paddingBottom: '16px'
        }}>
          <h2 style={{ margin: 0, color: '#333' }}>Special Times verwalten</h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '4px 8px'
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '16px',
            color: '#c33'
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '24px' }}>
          {/* Formular */}
          <div style={{ flex: 1, minWidth: '300px' }}>
            <h3 style={{ marginTop: 0, color: '#333' }}>
              {editingItem ? 'Special Time bearbeiten' : 'Neue Special Time erstellen'}
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Name:
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="z.B. Herbstferien"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Startdatum:
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Enddatum:
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Beschreibung (optional):
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Optionale Beschreibung..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Farbe:
              </label>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(4, 1fr)', 
                gap: '8px',
                marginBottom: '8px'
              }}>
                {predefinedColors.map(({ color, name }) => (
                  <button
                    key={color}
                    onClick={() => setFormData(prev => ({ ...prev, color }))}
                    style={{
                      backgroundColor: color,
                      border: formData.color === color ? '3px solid #333' : '1px solid #ddd',
                      borderRadius: '4px',
                      height: '40px',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                    title={name}
                  >
                    {formData.color === color && (
                      <span style={{ 
                        position: 'absolute', 
                        top: '50%', 
                        left: '50%', 
                        transform: 'translate(-50%, -50%)',
                        fontSize: '16px'
                      }}>
                        ✓
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <input
                type="color"
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  height: '40px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleSave}
                disabled={loading}
                style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {loading ? 'Speichere...' : (editingItem ? 'Aktualisieren' : 'Erstellen')}
              </button>
              
              {editingItem && (
                <button
                  onClick={resetForm}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Abbrechen
                </button>
              )}
            </div>
          </div>

          {/* Liste der Special Times */}
          <div style={{ flex: 1, minWidth: '400px' }}>
            <h3 style={{ marginTop: 0, color: '#333' }}>Vorhandene Special Times</h3>
            
            {loading && !specialTimes.length ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                Lade Special Times...
              </div>
            ) : specialTimes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                Keine Special Times vorhanden
              </div>
            ) : (
              <div style={{ 
                maxHeight: '400px', 
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}>
                {specialTimes.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      borderBottom: '1px solid #eee',
                      backgroundColor: editingItem?.id === item.id ? '#f8f9fa' : 'white'
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: item.color,
                        borderRadius: '4px',
                        marginRight: '12px',
                        border: '1px solid #ddd'
                      }}
                    />
                    
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {item.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {formatDate(item.start_date)} - {formatDate(item.end_date)}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        onClick={() => handleEdit(item)}
                        style={{
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Bearbeiten
                      </button>
                      
                      <button
                        onClick={() => handleDelete(item.id)}
                        disabled={loading}
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{
          marginTop: '20px',
          paddingTop: '16px',
          borderTop: '1px solid #eee',
          fontSize: '12px',
          color: '#666',
          textAlign: 'center'
        }}>
          Tastenkombination: Strg + Alt + A zum Öffnen/Schließen
        </div>
      </div>
    </div>
  );
};

export default SpecialTimesModal;